import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import { OrderInfo } from './order/OrderInfo';
import { Order } from '../../../models';
import { useNavigate, useQuery } from '../../../routes';
import { payment } from '../../../layout/routes';
import { useLaunchDarkly } from '../../../launchdarkly';
import { CompletePayment } from '../../../components';
import CancelOrderDialog from './CancelOrderDialog';
import { fromLayout, fromOrder, useAppDispatch } from '../../../store';
import { LoadingProgress } from '../../../layout';

const useStyles = makeStyles(({
  groupTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141D',
    margin: '0 0 15px',
  },
  actionBand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px 20px',
    padding: '0 30px 24px',
  },
  supportText: {
    fontSize: 13.5,
    color: '#5F5D75',
    '& a': {
      fontWeight: 600,
      color: '#0F8461',
      textDecoration: 'none',
    },
    '& a:hover': {
      color: '#0C6A4E',
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
    },
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
}));

const primaryButtonSx = {
  height: 44,
  padding: '0 24px',
  borderRadius: '10px',
  backgroundColor: '#0F8461',
  color: '#FFFFFF',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0B6B4E',
  },
} as const;

const dangerOutlinedButtonSx = {
  height: 44,
  padding: '0 22px',
  borderRadius: '10px',
  border: '1.5px solid #E6B8B5',
  backgroundColor: '#FFFFFF',
  color: '#D14343',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    border: '1.5px solid #D14343',
    backgroundColor: '#FBECEB',
  },
} as const;

export const PendingOrderHistory = (
  { pendingOrders }: { pendingOrders: Order[] },
) => {
  const classes = useStyles();
  const { flags } = useLaunchDarkly();
  const { queryDictionary } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const dispatch = useAppDispatch();
  const [showCancelOrderDialog, setShowCancelOrderDialog] = useState(false);
  const [selectedCancelOrder, setSelectedCancelOrder] = useState<Order>();
  const [cancelLoading, setCancelLoading] = useState(false);

  const cancelOrder = (order?: Order) => {
    setShowCancelOrderDialog(false);
    if (order) {
      const cancelOrder = async () => {
        setCancelLoading(true);
        await dispatch(fromOrder.doCancelOrder(order.id));
        setCancelLoading(false);
      };
      cancelOrder()
        .then(() => {
          dispatch(fromLayout.setRefetchData(true));
        })
        .catch(() => {});
    }
  };

  const showCancelDialog = (order: Order) => {
    setShowCancelOrderDialog(true);
    setSelectedCancelOrder(order);
  };

  const closeCancelDialog = () => {
    setShowCancelOrderDialog(false);
  };

  const renderSupportText = () => (
    <span className={classes.supportText}>
      <FormattedMessage
        id="page.order.history.having.trouble"
        values={{
          a: (chunk: React.ReactNode) => (
            <a target="_blank" href="mailto:success@katalon.com" rel="noreferrer">{chunk}</a>
          ),
        }}
      />
    </span>
  );

  const renderActionBtnForBT = (order: Order) => (
    <div id={`order-${order.id}-action-items`} className={classes.actionBand}>
      {renderSupportText()}
      <div className={classes.actionButtons}>
        <Button
          variant="contained"
          onClick={() => navigate(payment.path, replaceQuery(queryDictionary()))}
          sx={primaryButtonSx}
        >
          <FormattedMessage id="page.order.history.action.button.update.payment.method" />
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            // TODO
          }}
          sx={dangerOutlinedButtonSx}
        >
          <FormattedMessage id="page.order.history.action.button.request.cancel" />
        </Button>
      </div>
    </div>
  );

  const renderActionBtnForCreditCard = (order: Order) => (
    <div id={`order-${order.id}-action-items`} className={classes.actionBand}>
      {renderSupportText()}
      <div className={classes.actionButtons}>
        <CompletePayment
          order={order}
        />
        <Button
          variant="outlined"
          onClick={() => showCancelDialog(order)}
          sx={dangerOutlinedButtonSx}
        >
          <FormattedMessage id="page.order.history.action.button.cancel.order" />
        </Button>
      </div>
    </div>
  );

  const renderActionBtns = (order: Order) => (
    order.stripeInvoiceUrl ? renderActionBtnForCreditCard(order)
      : (flags?.orderHistoryActionBtnEnabled && renderActionBtnForBT(order))
  );

  const renderOrderDetails = () => pendingOrders.map(order => (
    <OrderInfo
      key={`pending-order-${order.id}`}
      order={order}
      actionItems={renderActionBtns(order)}
    />
  ));

  return (
    <Box
      id="pending-order-container"
      display="flex"
      flexDirection="column"
      paddingTop={2}
    >
      <Box id="pending-order-title">
        <Typography className={classes.groupTitle}>
          <FormattedMessage id="page.order.history.pending.order.title" />
        </Typography>
      </Box>
      {renderOrderDetails()}
      <CancelOrderDialog
        showDialog={showCancelOrderDialog}
        handleCancelOrder={() => cancelOrder(selectedCancelOrder)}
        handleCloseDialog={() => closeCancelDialog()}
      />
      {cancelLoading && <LoadingProgress fullscreen />}
    </Box>
  );
};
